import { Button } from "@/components/flexkit/Button";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { Input } from "@/components/podkit/forms/Input";
import { WorkflowCard } from "@/components/automations/AutomationCard";
import { WorkflowStatusFilter, WorkflowStatusFilterValue } from "@/components/automations/AutomationStatusFilter";
import {
    AutomationCreatorFilter,
    ALL_CREATORS_VALUE,
    type CreatorFilterValue,
} from "@/components/automations/AutomationCreatorFilter";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useListWorkflowExecutions, useListWorkflows, type PlainWorkflow } from "@/queries/automation-queries";
import { isWorkflowRunning, isWorkflowCompleted, workflowDetailsUrl, isWorkflowDeleting } from "@/utils/automations";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import type { AutomationTemplate } from "@/utils/automation-templates";
import { AutomationsListEmpty } from "@/components/automations/AutomationsListEmpty";
import { Search } from "lucide-react";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { matchSorter } from "match-sorter";
import { cn } from "@/components/podkit/lib/cn";

type WorkflowListProps = {
    onTemplateSelect: (template: AutomationTemplate) => void;
};

type WorkflowListItemProps = {
    workflow: PlainWorkflow;
    statusFilter: WorkflowStatusFilterValue;
    creatorFilter: CreatorFilterValue;
};

const WorkflowListItem: FC<WorkflowListItemProps> = ({ workflow, statusFilter, creatorFilter }) => {
    const navigate = useNavigate();
    const { data: executions } = useListWorkflowExecutions({
        enabled: true,
        workflowId: workflow.id,
    });

    const shouldShow = useMemo(() => {
        // Check status filter
        if (statusFilter !== WorkflowStatusFilterValue.All) {
            if (statusFilter === WorkflowStatusFilterValue.Running && !isWorkflowRunning(executions)) {
                return false;
            }
            if (statusFilter === WorkflowStatusFilterValue.Completed && !isWorkflowCompleted(executions)) {
                return false;
            }
        }

        // Check creator filter
        if (creatorFilter !== ALL_CREATORS_VALUE) {
            const creatorId = workflow.metadata?.creator?.id;
            if (creatorId !== creatorFilter) {
                return false;
            }
        }

        return true;
    }, [statusFilter, creatorFilter, executions, workflow.metadata?.creator?.id]);

    const isDeleting = isWorkflowDeleting(workflow);

    const handleClick = () => {
        if (!isDeleting) {
            navigate(workflowDetailsUrl(workflow));
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Handle Enter and Space for accessibility (standard button behavior)
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
        }
    };

    if (!shouldShow) {
        return null;
    }

    return (
        <div
            role="button"
            tabIndex={isDeleting ? -1 : 0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={cn(
                "rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                !isDeleting && "cursor-pointer"
            )}
            data-tracking-id="automation-list-item"
        >
            <WorkflowCard workflow={workflow} className={cn(!isDeleting && "hover:bg-surface-hover")} />
        </div>
    );
};

export const WorkflowList: FC<WorkflowListProps> = ({ onTemplateSelect }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<WorkflowStatusFilterValue>(WorkflowStatusFilterValue.All);
    const [creatorFilter, setCreatorFilter] = useState<CreatorFilterValue>(ALL_CREATORS_VALUE);
    const { isMobileViewport: isMobile, isPending: isPendingMobileViewport } = useIsMobileViewport();
    const { toast } = useToast();
    const hasShownErrorToast = useRef(false);

    const {
        data: workflows,
        error: workflowsError,
        isPending: isPendingWorkflows,
        refetch: refetchWorkflows,
    } = useListWorkflows();

    useEffect(() => {
        // Auto-focus search on desktop
        if (!isMobile && !isPendingMobileViewport) {
            inputRef.current?.focus();
        }
    }, [isMobile, isPendingMobileViewport]);

    useEffect(() => {
        if (workflowsError && !hasShownErrorToast.current) {
            hasShownErrorToast.current = true;
            toast({
                title: "Failed to load automations",
                description: formatError(workflowsError),
            });
        }
        // Reset the flag when error is cleared
        if (!workflowsError && hasShownErrorToast.current) {
            hasShownErrorToast.current = false;
        }
    }, [workflowsError, toast]);

    // Filter workflows by search query using match-sorter for better matching
    const searchFilteredWorkflows = useMemo(() => {
        if (!workflows) return [];
        if (!searchQuery.trim()) return workflows;

        return matchSorter(workflows, searchQuery, {
            keys: ["metadata.name", "metadata.description", "id"],
        });
    }, [workflows, searchQuery]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        const visibleItems = listRef.current?.querySelectorAll('[role="button"]');
        if (!visibleItems || visibleItems.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            const currentIndex = Array.from(visibleItems).findIndex((item) => item === document.activeElement);
            const nextIndex = currentIndex < visibleItems.length - 1 ? currentIndex + 1 : currentIndex;
            (visibleItems[nextIndex] as HTMLElement)?.focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const currentIndex = Array.from(visibleItems).findIndex((item) => item === document.activeElement);
            if (currentIndex <= 0) {
                // Focus back to search input
                inputRef.current?.focus();
            } else {
                const nextIndex = currentIndex - 1;
                (visibleItems[nextIndex] as HTMLElement)?.focus();
            }
        }
    };

    const showErrorState =
        !searchQuery && !isPendingWorkflows && workflowsError && (!workflows || workflows.length === 0);
    const showEmptyState = !searchQuery && !isPendingWorkflows && !workflowsError && workflows?.length === 0;

    if (showErrorState) {
        return (
            <EmptyStateCard
                title="Failed to load automations"
                description={
                    workflowsError ? formatError(workflowsError) : "There was an error loading your automations."
                }
                data-testid="workflows-error-state"
            >
                <Button
                    variant="secondary"
                    LeadingIcon={IconRefresh}
                    onClick={() => refetchWorkflows()}
                    data-tracking-id="retry-load-workflows"
                >
                    Retry
                </Button>
            </EmptyStateCard>
        );
    }

    if (showEmptyState) {
        return <AutomationsListEmpty onTemplateSelect={onTemplateSelect} />;
    }

    return (
        <div className="flex flex-col gap-3 @container" data-testid="workflows-list" onKeyDown={handleKeyDown}>
            <div className="flex flex-col gap-2 @md:flex-row @md:items-center">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-tertiary" />
                    <Input
                        ref={inputRef}
                        placeholder="Search automations"
                        className="max-w-none pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="automation-search-input"
                    />
                </div>
                <div className="flex items-stretch gap-2">
                    <AutomationCreatorFilter
                        value={creatorFilter}
                        onChange={setCreatorFilter}
                        workflows={workflows || []}
                    />
                    <WorkflowStatusFilter value={statusFilter} onChange={setStatusFilter} />
                </div>
            </div>

            {isPendingWorkflows ? (
                <LoadingState />
            ) : searchFilteredWorkflows.length === 0 && searchQuery.trim() ? (
                <EmptyStateCard
                    title="No matching automations"
                    description={`No automations match "${searchQuery}". Try a different search term.`}
                    data-testid="no-matching-automations-empty-state"
                />
            ) : (
                <div ref={listRef} className="flex flex-col gap-2" data-testid="workflows-list-items">
                    {searchFilteredWorkflows.map((workflow) => (
                        <WorkflowListItem
                            key={workflow.id}
                            workflow={workflow}
                            statusFilter={statusFilter}
                            creatorFilter={creatorFilter}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
